import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { User } from '../contexts/AuthContext';

interface AuditoriaTransito {
  id: number;
  titulo: string;
  client: { id: number; nombre: string; };
  branch?: { id: number; nombre: string; };
  operador?: { id: number; name: string; username: string; };
  supervisor?: { id: number; name: string; username: string; };
  estado: string;
  prioridad: string;
  fechaSolicitada: string;
  fechaAsignacion?: string;
  fechaInicio?: string;
  fechaPausa?: string;
  tiempoEstimadoMinutos?: number;
  observacionesOperador?: string;
  createdAt: string;
  updatedAt: string;
}

interface AuditoriasTranisitoSupervisorProps {
  user: User;
}

const AuditoriasTranisitoSupervisor: React.FC<AuditoriasTranisitoSupervisorProps> = ({ user }) => {
  const [auditorias, setAuditorias] = useState<AuditoriaTransito[]>([]);
  const [filteredAuditorias, setFilteredAuditorias] = useState<AuditoriaTransito[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [operadorFilter, setOperadorFilter] = useState('all');
  const [operadores, setOperadores] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReassignModal, setShowReassignModal] = useState<{audit: AuditoriaTransito} | null>(null);
  const [selectedOperator, setSelectedOperator] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAuditorias();
  }, [auditorias, searchTerm, estadoFilter, operadorFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar todas las auditorías
      const auditoriasRes = await api.get('/audits');
      
      // Cargar operadores y supervisores para filtros y reasignación
      const operadoresRes = await api.get('/users/assignable');
      const operadoresList = operadoresRes.data.success ? operadoresRes.data.data : [];
      
      setOperadores(operadoresList);
      
      if (auditoriasRes.data.success) {
        const todasAuditorias = auditoriasRes.data.data || [];
        
        // Mapear auditorías con información completa
        const auditoriasCompletas = todasAuditorias.map((audit: any) => ({
          ...audit,
          client: audit.client || audit.Client,
          branch: audit.branch || audit.Branch,
          operador: audit.operador || audit.Operador ? {
            ...(audit.operador || audit.Operador),
            name: (audit.operador || audit.Operador).name,
            username: (audit.operador || audit.Operador).username || (audit.operador || audit.Operador).email?.split('@')[0]
          } : null,
          supervisor: audit.supervisor || audit.Supervisor ? {
            ...(audit.supervisor || audit.Supervisor),
            name: (audit.supervisor || audit.Supervisor).name,
            username: (audit.supervisor || audit.Supervisor).username || (audit.supervisor || audit.Supervisor).email?.split('@')[0]
          } : null
        }));
        
        setAuditorias(auditoriasCompletas);
      }
    } catch (err: any) {
      setError('Error cargando datos: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const filterAuditorias = () => {
    let filtered = auditorias.filter(audit => {
      // Mostrar TODAS las auditorías en el flujo, incluyendo las que nadie tomó
      const estadosRelevantes = [
        'pendiente',      // ✅ Nadie las tomó todavía - ESTAS SON LAS IMPORTANTES
        'asignada',       // ✅ Asignadas pero no empezadas
        'en_proceso',     // ✅ En proceso con operador
        'pausada',        // ✅ Pausadas - el supervisor debe verlas
        'terminada',      // ✅ Terminadas, esperando revisión
        'solicitud'       // ✅ Recién creadas, esperando que alguien las tome
      ];
      return estadosRelevantes.includes(audit.estado);
    });

    // Filtro por término de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(audit =>
        audit.client?.nombre.toLowerCase().includes(term) ||
        audit.branch?.nombre?.toLowerCase().includes(term) ||
        audit.operador?.name?.toLowerCase().includes(term) ||
        audit.operador?.username?.toLowerCase().includes(term) ||
        audit.titulo?.toLowerCase().includes(term) ||
        audit.id.toString().includes(term)
      );
    }

    // Filtro por estado
    if (estadoFilter !== 'all') {
      filtered = filtered.filter(audit => audit.estado === estadoFilter);
    }

    // Filtro por operador
    if (operadorFilter !== 'all') {
      if (operadorFilter === 'sin_asignar') {
        filtered = filtered.filter(audit => !audit.operador);
      } else {
        filtered = filtered.filter(audit => audit.operador?.id?.toString() === operadorFilter);
      }
    }

    setFilteredAuditorias(filtered);
  };

  const getEstadoColor = (estado: string) => {
    const colores: { [key: string]: string } = {
      'solicitud': '#dc2626',   // rojo - NADIE LA TOMÓ
      'pendiente': '#f59e0b',   // naranja - DISPONIBLE 
      'asignada': '#3b82f6',    // azul - asignada
      'en_proceso': '#10b981',  // verde - trabajando
      'pausada': '#8b5cf6',     // morado - pausada
      'terminada': '#059669',   // verde oscuro - terminada
      'entregada': '#6b7280',   // gris - entregada
      'facturada': '#6b7280'    // gris - facturada
    };
    return colores[estado] || '#6b7280';
  };

  const getEstadoTexto = (audit: AuditoriaTransito) => {
    switch (audit.estado) {
      case 'solicitud':
        return '🆕 Nueva solicitud - NADIE LA TOMÓ';
      case 'pendiente':
        return '⏳ DISPONIBLE - Nadie la tomó todavía';
      case 'asignada':
        return `📋 Asignada a ${audit.operador?.name || 'operador desconocido'}`;
      case 'en_proceso':
        return `⚡ Trabajando - ${audit.operador?.name || 'operador desconocido'}`;
      case 'pausada':
        return `⏸️ PAUSADA - ${audit.operador?.name || 'operador desconocido'}`;
      case 'terminada':
        return `✅ Terminada - Esperando revisión`;
      default:
        return `❓ Estado: ${audit.estado}`;
    }
  };

  const tomarAuditoria = async (auditId: number) => {
    try {
      const response = await api.post(`/audits/${auditId}/take`);
      if (response.data.success) {
        // SIN cartel al tomar auditoría - solo recargar datos
        loadData();
      }
    } catch (err: any) {
      setError('Error tomando auditoría: ' + (err.response?.data?.error || err.message));
    }
  };

  const reasignarAuditoria = async (auditId: number, newOperatorId: string) => {
    try {
      const response = await api.put(`/audits/${auditId}/reassign`, {
        operadorId: parseInt(newOperatorId)
      });
      if (response.data.success) {
        setSuccess(`Auditoría #${auditId} reasignada exitosamente`);
        setShowReassignModal(null);
        setSelectedOperator('');
        loadData();
      }
    } catch (err: any) {
      setError('Error reasignando auditoría: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReassignClick = (audit: AuditoriaTransito) => {
    setShowReassignModal({ audit });
    setSelectedOperator('');
  };

  const handleReassignConfirm = () => {
    if (showReassignModal && selectedOperator) {
      reasignarAuditoria(showReassignModal.audit.id, selectedOperator);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Argentina/Buenos_Aires'
    });
  };

  const getTiempoTranscurrido = (fechaInicio: string) => {
    if (!fechaInicio) return '';
    const inicio = new Date(fechaInicio);
    const ahora = new Date();
    const diff = ahora.getTime() - inicio.getTime();
    const horas = Math.floor(diff / (1000 * 60 * 60));
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${horas}h ${minutos}m`;
  };

  if (loading) return <div className="loading">Cargando auditorías en tránsito...</div>;

  return (
    <div className="auditorias-transito-container">
      <div className="header">
        <h2>🚦 Control de Auditorías en Tránsito</h2>
        <p>Panel de control para supervisar el estado y ubicación de todas las auditorías</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filtros de búsqueda */}
      <div className="filtros-container">
        <div className="filtros-row">
          <div className="filtro-group">
            <label>🔍 Buscar por Cliente, Negocio, Operador o ID:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Ej: ABC Empresa, Juan, Sucursal Centro, 123"
              className="search-input"
            />
          </div>

          <div className="filtro-group">
            <label>📊 Estado:</label>
            <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)}>
              <option value="all">Todos los estados</option>
              <option value="pendiente">⏳ Pendientes</option>
              <option value="asignada">📋 Asignadas</option>
              <option value="en_proceso">⚡ En Proceso</option>
              <option value="pausada">⏸️ Pausadas</option>
              <option value="terminada">✅ Terminadas</option>
            </select>
          </div>

          <div className="filtro-group">
            <label>👤 Operador:</label>
            <select value={operadorFilter} onChange={(e) => setOperadorFilter(e.target.value)}>
              <option value="all">Todos los operadores</option>
              <option value="sin_asignar">🚫 Sin asignar</option>
              {operadores.map(op => (
                <option key={op.id} value={op.id.toString()}>
                  {op.name} ({op.username})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="resultados-info">
          Mostrando {filteredAuditorias.length} de {auditorias.length} auditorías
        </div>
      </div>

      {/* Lista de auditorías */}
      <div className="auditorias-list">
        {filteredAuditorias.length === 0 ? (
          <div className="no-auditorias">
            <p>No se encontraron auditorías con los filtros seleccionados</p>
          </div>
        ) : (
          filteredAuditorias.map(audit => (
            <div key={audit.id} className="auditoria-card">
              <div className="auditoria-header">
                <div className="auditoria-id">#{audit.id}</div>
                <div className="auditoria-cliente">
                  <strong>{audit.client?.nombre || 'Cliente desconocido'}</strong>
                  {audit.branch?.nombre && (
                    <span className="sucursal"> - {audit.branch.nombre}</span>
                  )}
                </div>
                <div 
                  className="auditoria-estado"
                  style={{ backgroundColor: getEstadoColor(audit.estado) }}
                >
                  {getEstadoTexto(audit)}
                </div>
              </div>

              <div className="auditoria-details">
                <div className="detail-row">
                  
                  {audit.fechaAsignacion && (
                    <div className="detail-item">
                      <span className="label">📋 Asignada:</span>
                      <span>{formatDate(audit.fechaAsignacion)}</span>
                    </div>
                  )}
                  
                  {audit.fechaInicio && (
                    <div className="detail-item">
                      <span className="label">⏱️ Tiempo transcurrido:</span>
                      <span>{getTiempoTranscurrido(audit.fechaInicio)}</span>
                    </div>
                  )}
                  
                  {audit.prioridad !== 'normal' && (
                    <div className="detail-item priority">
                      <span className="label">🚨 Prioridad:</span>
                      <span className="priority-badge">{audit.prioridad.toUpperCase()}</span>
                    </div>
                  )}
                </div>

                {audit.observacionesOperador && (
                  <div className="observaciones">
                    <span className="label">💬 Observaciones:</span>
                    <span>{audit.observacionesOperador}</span>
                  </div>
                )}

                <div className="auditoria-actions">
                  {(audit.estado === 'solicitud' || audit.estado === 'pendiente') && (
                    <button 
                      className="btn-tomar urgente"
                      onClick={() => tomarAuditoria(audit.id)}
                    >
                      🚨 TOMAR AHORA
                    </button>
                  )}
                  
                  <button className="btn-detalles">
                    👁️ Ver Detalles
                  </button>
                  
                  {(audit.estado === 'asignada' || audit.estado === 'en_proceso' || audit.estado === 'pausada') && (
                    <button 
                      className="btn-reasignar"
                      onClick={() => handleReassignClick(audit)}
                    >
                      🔄 Reasignar
                    </button>
                  )}

                  {audit.estado === 'terminada' && (
                    <button className="btn-revisar">
                      ✅ Revisar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de reasignación */}
      {showReassignModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>🔄 Reasignar Auditoría #{showReassignModal.audit.id}</h3>
            <p><strong>Cliente:</strong> {showReassignModal.audit.client.nombre}</p>
            <p><strong>Título:</strong> {showReassignModal.audit.titulo}</p>
            <p><strong>Operador actual:</strong> {showReassignModal.audit.operador?.name || 'Sin asignar'}</p>
            
            <div className="form-group">
              <label htmlFor="newOperator">Nuevo Operador:</label>
              <select 
                id="newOperator"
                value={selectedOperator} 
                onChange={(e) => setSelectedOperator(e.target.value)}
                className="operator-select"
              >
                <option value="">Seleccionar operador...</option>
                {operadores
                  .filter(op => op.id !== showReassignModal.audit.operador?.id)
                  .map(op => (
                  <option key={op.id} value={op.id.toString()}>
                    {op.name} ({op.username})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setShowReassignModal(null)}
              >
                Cancelar
              </button>
              <button 
                className="btn-confirm"
                onClick={handleReassignConfirm}
                disabled={!selectedOperator}
              >
                Reasignar
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .auditorias-transito-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .header {
          margin-bottom: 30px;
          text-align: center;
        }

        .header h2 {
          margin: 0 0 10px 0;
          color: #1f2937;
          font-size: 28px;
        }

        .header p {
          color: #6b7280;
          margin: 0;
        }

        .alert {
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .alert-error {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .alert-success {
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }

        .filtros-container {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }

        .filtros-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 20px;
          margin-bottom: 15px;
        }

        .filtro-group {
          display: flex;
          flex-direction: column;
        }

        .filtro-group label {
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
          font-size: 14px;
        }

        .search-input, select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .search-input:focus, select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .resultados-info {
          color: #6b7280;
          font-size: 14px;
          text-align: center;
        }

        .auditorias-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .auditoria-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          overflow: hidden;
          border-left: 4px solid #3b82f6;
        }

        .auditoria-header {
          display: flex;
          align-items: center;
          padding: 15px 20px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          gap: 15px;
        }

        .auditoria-id {
          font-weight: bold;
          color: #1f2937;
          font-size: 16px;
        }

        .auditoria-cliente {
          flex: 1;
          font-size: 16px;
          color: #1f2937;
        }

        .sucursal {
          color: #6b7280;
          font-weight: normal;
        }

        .auditoria-estado {
          padding: 6px 12px;
          border-radius: 20px;
          color: white;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .auditoria-details {
          padding: 20px;
        }

        .detail-row {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 15px;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        .detail-item .label {
          color: #6b7280;
          font-weight: 500;
        }

        .priority-badge {
          background: #dc2626;
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: bold;
        }

        .observaciones {
          background: #f3f4f6;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 15px;
          font-size: 14px;
        }

        .observaciones .label {
          color: #6b7280;
          font-weight: 500;
          margin-right: 8px;
        }

        .auditoria-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        button {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          font-weight: 500;
        }

        .btn-tomar {
          background: #10b981;
          color: white;
        }

        .btn-tomar.urgente {
          background: #dc2626;
          color: white;
          font-weight: bold;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
        }

        .btn-detalles {
          background: #3b82f6;
          color: white;
        }

        .btn-reasignar {
          background: #f59e0b;
          color: white;
        }

        .btn-revisar {
          background: #059669;
          color: white;
        }

        button:hover {
          opacity: 0.9;
        }

        .no-auditorias {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          background: white;
          border-radius: 8px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          max-width: 500px;
          width: 90%;
        }

        .modal-content h3 {
          margin: 0 0 20px 0;
          color: #1f2937;
          font-size: 1.5em;
        }

        .form-group {
          margin: 20px 0;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #374151;
        }

        .operator-select {
          width: 100%;
          padding: 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .modal-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 25px;
        }

        .btn-cancel {
          background: #6b7280;
          color: white;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        .btn-confirm {
          background: #f59e0b;
          color: white;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        .btn-confirm:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .filtros-row {
            grid-template-columns: 1fr;
          }
          
          .auditoria-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          
          .detail-row {
            flex-direction: column;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default AuditoriasTranisitoSupervisor;